import { useEffect, useMemo, useState, type DragEvent } from 'react';

import { entityKind, entityName, hierarchyRows, type EntityDesc } from '../../lib/scene-doc.js';
import { sceneClipboard, useScene } from '../../state/scene.js';
import { ContextMenu, MenuButton, isTyping, type MenuItem } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { newEntityTemplate } from './component-schemas.js';
import { hierarchyAssetDrop } from './AssetDrop.js';
import { useExpandedScene } from './inspector-context.js';
import { hierarchyPrefabItems } from './prefab-menus.js';
import { ActiveSceneHeader, AdditiveSceneSections } from './SceneSections.js';

const KIND_ICON: Record<ReturnType<typeof entityKind>, IconName> = {
  light: 'light',
  camera: 'camera',
  mesh: 'cube',
  terrain: 'mountain',
  audio: 'audio',
  particles: 'particles',
  liquid: 'water',
  empty: 'empty',
};

export function createMenuItems(parent: string | null): MenuItem[] {
  const add = (kind: string) => () => {
    const t = newEntityTemplate(kind);
    useScene.getState().apply({
      cmd: 'addEntity',
      desc: { id: '', name: t.name, parent, transform: { position: [0, 0, 0] }, components: t.components },
      parent,
    });
  };
  return [
    { id: 'h-obj', label: parent ? 'Create child' : 'Create', heading: true },
    { id: 'empty', label: 'Empty', icon: 'empty', run: add('empty') },
    { id: 'cube', label: 'Cube', icon: 'cube', run: add('cube') },
    { id: 'sphere', label: 'Sphere', icon: 'sphere', run: add('sphere') },
    { id: 'capsule', label: 'Capsule', icon: 'cube', run: add('capsule') },
    { id: 'cylinder', label: 'Cylinder', icon: 'cube', run: add('cylinder') },
    { id: 'plane', label: 'Plane', icon: 'grid', run: add('plane') },
    { id: 'sep1', label: '', separator: true },
    { id: 'pl', label: 'Point Light', icon: 'light', run: add('pointLight') },
    { id: 'sl', label: 'Spot Light', icon: 'light', run: add('spotLight') },
    { id: 'dl', label: 'Directional Light', icon: 'sun', run: add('directionalLight') },
    { id: 'cam', label: 'Camera', icon: 'camera', run: add('camera') },
    { id: 'ps', label: 'Particle System', icon: 'particles', run: add('particleSystem') },
    { id: 'water', label: 'Water (liquid)', icon: 'water', run: add('water') },
    { id: 'lava', label: 'Lava (liquid)', icon: 'water', run: add('lava') },
    { id: 'decal', label: 'Decal (projector)', icon: 'image', run: add('decal') },
    { id: 'sep2', label: '', separator: true },
    { id: 'sprite', label: 'Sprite (2D)', icon: 'pixel', run: add('sprite') },
    { id: 'tilemap', label: 'Tilemap (2D)', icon: 'grid', run: add('tilemap') },
  ];
}

/** Scene hierarchy: entity tree with selection, rename, drag-to-reparent. */
export function Hierarchy() {
  const doc = useScene((s) => s.doc);
  const selection = useScene((s) => s.selection);
  const collapsedList = useScene((s) => s.collapsed);
  const apply = useScene((s) => s.apply);
  const select = useScene((s) => s.select);
  const toggleCollapsed = useScene((s) => s.toggleCollapsed);

  const [filter, setFilter] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; id: string | null } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<string | null>(null);

  const collapsed = useMemo(() => new Set(collapsedList), [collapsedList]);
  // Prefab instances are expanded: their entities follow them (edited as overrides).
  const expanded = useExpandedScene();
  const rows = useMemo(() => {
    if (!doc) return [];
    const all = hierarchyRows(expanded ? { ...doc, entities: expanded.entities } : doc, filter ? new Set() : collapsed);
    if (!filter) return all;
    const needle = filter.toLowerCase();
    return all.filter((r) => entityName(r.entity).toLowerCase().includes(needle) || r.entity.id.includes(needle));
  }, [doc, expanded, collapsed, filter]);

  if (!doc) return null;

  function onRowClick(entity: EntityDesc, event: React.MouseEvent) {
    if (event.ctrlKey || event.metaKey) {
      select(selection.includes(entity.id) ? selection.filter((s) => s !== entity.id) : [...selection, entity.id]);
      setAnchor(entity.id);
    } else if (event.shiftKey && anchor) {
      const ids = rows.map((r) => r.entity.id);
      const a = ids.indexOf(anchor);
      const b = ids.indexOf(entity.id);
      if (a >= 0 && b >= 0) select(ids.slice(Math.min(a, b), Math.max(a, b) + 1));
    } else {
      select([entity.id]);
      setAnchor(entity.id);
    }
  }

  function onDrop(event: DragEvent, parent: string | null) {
    event.preventDefault();
    setDropTarget(null);
    const id = event.dataTransfer.getData('application/x-prism-entity');
    if (!id && hierarchyAssetDrop(event.dataTransfer, parent)) return;
    if (!id || id === parent) return;
    apply({ cmd: 'reparent', id, parent });
  }

  function clipboardItems(id: string | null): MenuItem[] {
    const s = useScene.getState();
    const empty = sceneClipboard().length === 0;
    return [
      ...(id ? [{ id: 'copy', label: 'Copy', icon: 'copy' as const, shortcut: 'Ctrl+C', run: () => void s.copy() }] : []),
      { id: 'paste', label: 'Paste', icon: 'file', shortcut: 'Ctrl+V', disabled: empty, run: () => void s.paste() },
      ...(id
        ? [
            { id: 'paste-child', label: 'Paste as Child', icon: 'file' as const, shortcut: 'Ctrl+Shift+V', disabled: empty, run: () => void s.paste({ asChild: true }) },
            {
              id: 'paste-child-local',
              label: 'Paste as Child (Keep Local Transform)',
              icon: 'file' as const,
              disabled: empty,
              run: () => void s.paste({ asChild: true, keepWorld: false }),
            },
          ]
        : []),
    ];
  }

  function itemsFor(id: string | null): MenuItem[] {
    if (!id) return [...clipboardItems(null), { id: 'sep0', label: '', separator: true }, ...createMenuItems(null)];
    const entity = doc!.entities.find((e) => e.id === id);
    const inPrefab = !entity;
    const count = selection.length > 1 && selection.includes(id) ? selection.length : 1;
    const s = useScene.getState();
    return [
      ...(count === 1 ? [{ id: 'rename', label: 'Rename', icon: 'edit' as const, shortcut: 'F2', run: () => setRenaming(id) }] : []),
      ...clipboardItems(id),
      { id: 'sep-c', label: '', separator: true },
      ...(inPrefab
        ? []
        : [
            {
              id: 'dup',
              label: count > 1 ? `Duplicate ${count}` : 'Duplicate',
              icon: 'copy' as const,
              shortcut: 'Ctrl+D',
              run: () => (count > 1 ? s.duplicateSelection() : apply({ cmd: 'duplicate', id })),
            },
          ]),
      ...(entity?.parent && count === 1
        ? [{ id: 'unparent', label: 'Move to root', icon: 'arrowUp' as const, run: () => apply({ cmd: 'reparent', id, parent: null }) }]
        : []),
      {
        id: 'del',
        label: count > 1 ? `Delete ${count}` : 'Delete',
        icon: 'trash',
        shortcut: 'Del',
        danger: true,
        run: () => (count > 1 ? s.deleteSelection() : apply({ cmd: 'deleteEntity', id })),
      },
      ...hierarchyPrefabItems(id, count),
      { id: 'sep', label: '', separator: true },
      ...createMenuItems(id),
    ];
  }

  return (
    <div className="hierarchy">
      <div className="panel-tools">
        <div className="search-input">
          <Icon name="search" size={13} />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search entities" aria-label="Search entities" />
        </div>
        <MenuButton items={() => createMenuItems(null)} className="ibtn" title="Create entity">
          <Icon name="plus" size={15} />
        </MenuButton>
      </div>

      <ActiveSceneHeader />
      <ul
        className={`tree-list${dropTarget === '__root' ? ' is-drop-root' : ''}`}
        role="tree"
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('.tree-item')) return;
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY, id: null });
        }}
        onDragOver={(e) => {
          if (!(e.target as HTMLElement).closest('.tree-item')) {
            e.preventDefault();
            setDropTarget('__root');
          }
        }}
        onDragLeave={() => setDropTarget(null)}
        onDrop={(e) => {
          if (!(e.target as HTMLElement).closest('.tree-item')) onDrop(e, null);
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) select([]);
        }}
      >
        {rows.map(({ entity, depth, hasChildren }) => {
          const selected = selection.includes(entity.id);
          const isCollapsed = collapsed.has(entity.id);
          const prefab = expanded?.status.get(entity.id);
          const inPrefab = prefab === 'child' || prefab === 'childOverridden' || prefab === 'nested';
          return (
            <li
              key={entity.id}
              role="treeitem"
              aria-selected={selected}
              aria-expanded={hasChildren ? !isCollapsed : undefined}
              className={`tree-item${selected ? ' is-selected' : ''}${dropTarget === entity.id ? ' is-drop' : ''}${prefab ? ` prefab-${prefab}` : ''}`}
              style={{ paddingLeft: 6 + depth * 14 }}
              draggable={renaming !== entity.id && !inPrefab}
              onDragStart={(e) => {
                e.dataTransfer.setData('application/x-prism-entity', entity.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropTarget(entity.id);
              }}
              onDragLeave={() => setDropTarget((t) => (t === entity.id ? null : t))}
              onDrop={(e) => {
                e.stopPropagation();
                onDrop(e, entity.id);
              }}
              onClick={(e) => onRowClick(entity, e)}
              onDoubleClick={() => setRenaming(entity.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (!selected) select([entity.id]);
                setMenu({ x: e.clientX, y: e.clientY, id: entity.id });
              }}
              title={prefab === 'missing' ? `${entity.id}: prefab missing` : prefab === 'overridden' ? `${entity.id}: prefab instance with overrides` : entity.id}
            >
              <button
                type="button"
                className={`tree-caret${hasChildren ? '' : ' is-leaf'}`}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) toggleCollapsed(entity.id);
                }}
              >
                {hasChildren && <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} size={11} />}
              </button>
              <Icon
                name={prefab === 'instance' || prefab === 'overridden' || prefab === 'missing' || prefab === 'nested' ? 'prefab' : KIND_ICON[entityKind(entity)]}
                size={14}
                className={`tree-kind kind-${entityKind(entity)}`}
              />
              {renaming === entity.id ? (
                <input
                  className="tree-rename"
                  autoFocus
                  defaultValue={entityName(entity)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.currentTarget.select()}
                  onBlur={(e) => {
                    const name = e.target.value.trim();
                    if (name && name !== entityName(entity)) apply({ cmd: 'renameEntity', id: entity.id, name });
                    setRenaming(null);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                />
              ) : (
                <span className="tree-label">{entityName(entity)}</span>
              )}
            </li>
          );
        })}
        {rows.length === 0 && (
          <li className="tree-empty muted">
            {filter ? 'No matching entities.' : 'Empty scene. Right-click or use + to add an entity.'}
          </li>
        )}
      </ul>

      <AdditiveSceneSections />
      {menu && <ContextMenu x={menu.x} y={menu.y} items={itemsFor(menu.id)} onClose={() => setMenu(null)} />}
      <RenameShortcut onRename={setRenaming} />
    </div>
  );
}

/** F2 renames the selected entity; listens on the window while mounted. */
function RenameShortcut({ onRename }: { onRename: (id: string) => void }) {
  const selection = useScene((s) => s.selection);
  useWindowKey('F2', () => {
    if (selection.length === 1) onRename(selection[0]!);
  });
  return null;
}

function useWindowKey(key: string, run: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== key || isTyping(e.target)) return;
      const root = document.querySelector('.tab-scene');
      if (!root || (root as HTMLElement).offsetParent === null) return;
      e.preventDefault();
      run();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, run]);
}
